import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Heading, Tab, TabList, Tabs, Text, useTheme,
} from '@chakra-ui/react';
import {
  useGetCurrentTenantAccessJobQuery,
  useGetTenantAccessAppsQuery,
  useGetTenantAccessJobQuery,
  usePreviewTenantAccessQuery,
  useRetryTenantAccessJobMutation,
  useSearchTenantAccessTenantsQuery,
  useSearchTenantAccessUsersQuery,
  useStartTenantAccessJobMutation,
  useValidateTenantIdsMutation,
} from '../services/apis/tenantAccessAPI';
import routes from '../core/routes';
import { canReachStep } from '../utils/tenantAccess';
import UserTab from '../components/TenantAccess/UserTab';
import TenantTab from '../components/TenantAccess/TenantTab';
import RoleTab from '../components/TenantAccess/RoleTab';
import ReviewTab from '../components/TenantAccess/ReviewTab';
import AuditLog from '../components/TenantAccess/AuditLog';
import { Stepper, SummaryPanel } from '../components/TenantAccess/TenantAccessLayout';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const TenantAccess = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const requestedJobId = searchParams.get('job');
  const auditTab = searchParams.get('tab') === 'audit';
  const auditJobId = searchParams.get('auditJob') || '';
  const [step, setStep] = useState(1);
  const [focusedJobId, setFocusedJobId] = useState(requestedJobId);
  const [hideCurrent, setHideCurrent] = useState(false);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [checkSummary, setCheckSummary] = useState(null);
  const [tenantMode, setTenantMode] = useState('paste');
  const [userQuery, setUserQuery] = useState('');
  const [tenantQuery, setTenantQuery] = useState('');
  const [appRoles, setAppRoles] = useState({});

  const debouncedUserQuery = useDebouncedValue(userQuery);
  const debouncedTenantQuery = useDebouncedValue(tenantQuery);
  const appRoleEntries = Object.entries(appRoles).filter(([, role]) => role);

  const userSearch = useSearchTenantAccessUsersQuery(
    { query: debouncedUserQuery.trim(), page_number: 1, page_size: 100 },
    { skip: !debouncedUserQuery.trim() },
  );
  const tenantSearch = useSearchTenantAccessTenantsQuery(
    { query: debouncedTenantQuery.trim(), page_number: 1, page_size: 100 },
    { skip: tenantMode !== 'search' || !debouncedTenantQuery.trim() },
  );
  const appsQuery = useGetTenantAccessAppsQuery(undefined, { skip: step < 3 });
  const previewBody = {
    user_ids: users.map((user) => user.id),
    tenant_ids: tenants.map((tenant) => tenant.id),
    app_roles: appRoleEntries.map(([app, role]) => ({ app, role })),
  };
  const previewQuery = usePreviewTenantAccessQuery(previewBody, {
    skip: step !== 4 || appRoleEntries.length === 0 || Boolean(focusedJobId),
  });
  const currentJobQuery = useGetCurrentTenantAccessJobQuery(undefined, {
    skip: Boolean(requestedJobId) || hideCurrent || Boolean(focusedJobId),
    refetchOnMountOrArgChange: true,
  });
  const jobId = focusedJobId || currentJobQuery.data?.data?.id || null;
  const [pollInterval, setPollInterval] = useState(2000);
  const jobQuery = useGetTenantAccessJobQuery(jobId, {
    skip: !jobId,
    pollingInterval: pollInterval,
  });
  const [startJob, startState] = useStartTenantAccessJobMutation();
  const [retryJob, retryState] = useRetryTenantAccessJobMutation();
  const [validateIds, validateState] = useValidateTenantIdsMutation();

  useEffect(() => {
    const status = jobQuery.data?.status;
    setPollInterval(!status || status === 'running' ? 2000 : 0);
  }, [jobQuery.data?.status]);

  const resetWizard = () => {
    setStep(1);
    setUsers([]);
    setTenants([]);
    setInvalid([]);
    setPasteText('');
    setCheckSummary(null);
    setTenantMode('paste');
    setUserQuery('');
    setTenantQuery('');
    setAppRoles({});
    setFocusedJobId(null);
    setHideCurrent(true);
    if (location.search) {
      navigate(routes.tenantAccess.url, { replace: true });
    }
  };

  const runAssignment = async () => {
    try {
      const result = await startJob(previewBody).unwrap();
      setHideCurrent(false);
      setFocusedJobId(result.id);
    } catch {
      setFocusedJobId(null);
    }
  };

  const retryAssignment = async () => {
    try {
      const result = await retryJob(jobId).unwrap();
      setFocusedJobId(result.id);
    } catch {
      // The job view stays on the current assignment when retry fails.
    }
  };

  const progress = {
    userCount: users.length,
    tenantCount: tenants.length,
    invalidCount: invalid.length,
    appCount: appRoleEntries.length,
  };
  const canReach = (target) => canReachStep(target, progress);

  const goTo = (target) => {
    if (canReach(target)) {
      setStep(target);
    }
  };

  const addUser = (user) => {
    setUsers((current) => (
      current.some((item) => item.id === user.id) ? current : [...current, user]
    ));
  };

  const toggleTenant = (tenant) => {
    setTenants((current) => {
      const exists = current.some((item) => item.id === tenant.id);
      return exists ? current.filter((item) => item.id !== tenant.id) : [...current, tenant];
    });
  };

  const checkIds = async () => {
    try {
      const result = await validateIds({
        ids: [pasteText],
        already_selected_ids: tenants.map((tenant) => tenant.id),
      }).unwrap();
      setTenants((current) => {
        const known = new Set(current.map((tenant) => tenant.id.toLowerCase()));
        const next = [...current];
        result.added.forEach((tenant) => {
          if (!known.has(tenant.id.toLowerCase())) {
            next.push(tenant);
            known.add(tenant.id.toLowerCase());
          }
        });
        return next;
      });
      setInvalid(result.invalid);
      setPasteText(result.invalid.map((item) => item.id).join('\n'));
      setCheckSummary({
        added_count: result.added_count,
        skipped_count: result.skipped_count,
      });
    } catch {
      setCheckSummary(null);
    }
  };

  // ?job= stays the assignment status view. The audit tab is filtered with auditJob.
  const openAudit = (jobFilter) => {
    const next = new URLSearchParams();
    next.set('tab', 'audit');
    if (jobFilter) {
      next.set('auditJob', jobFilter);
    }
    navigate(`${routes.tenantAccess.url}?${next.toString()}`);
  };

  return (
    <Box maxW="1180px" mx="auto" pb={16}>
      <Text fontSize="xs" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" color="gray.500">
        Exchange Administration
      </Text>
      <Heading as="h1" {...theme.styles.global.h1} mt={1}>Tenant Access</Heading>
      <Text color="gray.600" maxW="62ch" mt={1}>
        Add existing users to districts and charters and set their app roles, in bulk.
      </Text>
      <Tabs
        index={auditTab ? 1 : 0}
        onChange={(index) => {
          if (index === 1) {
            openAudit(auditJobId);
          } else {
            navigate(routes.tenantAccess.url);
          }
        }}
        mt={4}
      >
        <TabList>
          <Tab>Assignment</Tab>
          <Tab>Audit log</Tab>
        </TabList>
      </Tabs>
      {auditTab ? (
        <Box mt={4}>
          <AuditLog initialJobId={auditJobId} />
        </Box>
      ) : (
        <>
          <Stepper step={step} canReach={canReach} onStep={goTo} counts={progress} />
          <Flex
            gap={6}
            align="flex-start"
            direction={{ base: 'column', lg: 'row' }}
          >
            <Box flex="1" minW="0">
              {jobId && (
                <ReviewTab
                  job={jobQuery.data}
                  isFetching={jobQuery.isFetching && !jobQuery.data}
                  isError={jobQuery.isError}
                  error={jobQuery.error}
                  onRetry={retryAssignment}
                  onStartNew={resetWizard}
                  onViewAudit={() => openAudit(jobId)}
                  isRetrying={retryState.isLoading}
                />
              )}
              {!jobId && step === 1 && (
                <UserTab
                  query={userQuery}
                  onQueryChange={setUserQuery}
                  results={userSearch.data?.data}
                  isFetching={userSearch.isFetching
                    || userQuery.trim() !== debouncedUserQuery.trim()}
                  isError={userSearch.isError}
                  error={userSearch.error}
                  selected={users}
                  onAdd={addUser}
                  onRemove={(id) => setUsers((current) => (
                    current.filter((user) => user.id !== id)
                  ))}
                  onClear={() => setUsers([])}
                  canContinue={canReach(2)}
                  onContinue={() => goTo(2)}
                />
              )}
              {!jobId && step === 2 && (
                <TenantTab
                  mode={tenantMode}
                  onMode={setTenantMode}
                  pasteText={pasteText}
                  onPasteText={setPasteText}
                  onCheck={checkIds}
                  checking={validateState.isLoading}
                  checkError={validateState.error}
                  checkSummary={checkSummary}
                  invalid={invalid}
                  onClearInvalid={() => {
                    setInvalid([]);
                    setPasteText('');
                    setCheckSummary((current) => (
                      current ? { added_count: current.added_count, skipped_count: 0 } : null
                    ));
                  }}
                  query={tenantQuery}
                  onQueryChange={setTenantQuery}
                  results={tenantSearch.data?.data}
                  isFetching={
                    tenantSearch.isFetching || tenantQuery.trim() !== debouncedTenantQuery.trim()
                  }
                  isError={tenantSearch.isError}
                  error={tenantSearch.error}
                  selected={tenants}
                  onToggle={toggleTenant}
                  onRemove={(id) => setTenants((current) => (
                    current.filter((tenant) => tenant.id !== id)
                  ))}
                  onClear={() => setTenants([])}
                  canContinue={canReach(3)}
                  onBack={() => goTo(1)}
                  onContinue={() => goTo(3)}
                />
              )}
              {!jobId && step === 3 && (
                <RoleTab
                  apps={appsQuery.data?.data}
                  isFetching={appsQuery.isFetching}
                  isError={appsQuery.isError}
                  error={appsQuery.error}
                  appRoles={appRoles}
                  onRole={(app, role) => {
                    setAppRoles((current) => {
                      const next = { ...current };
                      if (role) {
                        next[app] = role;
                      } else {
                        delete next[app];
                      }
                      return next;
                    });
                  }}
                  canContinue={canReach(4)}
                  onBack={() => goTo(2)}
                  onContinue={() => goTo(4)}
                />
              )}
              {!jobId && step === 4 && (
                <ReviewTab
                  preview={previewQuery.data}
                  isFetching={previewQuery.isFetching}
                  isError={previewQuery.isError}
                  error={previewQuery.error}
                  onBack={() => goTo(3)}
                  onRun={runAssignment}
                  isStarting={startState.isLoading}
                />
              )}
            </Box>
            <Box w={{ base: '100%', lg: '290px' }} flexShrink={0}>
              <SummaryPanel
                users={users}
                tenants={tenants}
                appRoles={Object.fromEntries(appRoleEntries)}
              />
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default TenantAccess;
